import buildType from '@jetbrains/icons/buildType'
import pipeline from '@jetbrains/icons/pipeline'
import project from '@jetbrains/icons/project'
import Icon from '@jetbrains/ring-ui-built/components/icon/icon'
import type {ListDataItem} from '@jetbrains/ring-ui-built/components/list/consts'
import Tag from '@jetbrains/ring-ui-built/components/tag/tag'
import {trackEvent} from '@jetbrains/teamcity-ui/statistics'
import {use, useMemo} from 'react'
import {useLinkClickHandler} from 'react-router'

import {useAppSelector} from '../../../hooks/react-redux'
import {useProjectId} from '../../../hooks/routes'
import useStatusSubscription from '../../../hooks/useStatusSubscription'
import {getIsReadOnly, getStatus} from '../../../selectors'
import {getProjectStatusRequest, ROOT_PROJECT_ID} from '../../../types'
import type {ProjectId, ProjectOrBuildTypeStatus} from '../../../types'
import {
  isCreateBuildTypeNewUiEnabled,
  isCreateProjectNewUiEnabled,
  isEarlyAccessLabelEnabled,
} from '../../../utils/featureToggles'
import {projectCreateHint} from '../../packages/Hints/HintItems/Hints.projectOverview'
import {useHint} from '../../packages/Hints/Hints.hooks'
import {ProjectsTreeContext} from '../ProjectsTree/ProjectsTree.context'
import {useSequenceLoaderReadyHandler} from '../SequenceLoader/SequenceLoader.hooks'

import styles from './CreateEntityButton.module.css'

import {getCreateBuildTypeHref, getCreatePipelineHref, getCreateProjectHref} from 'src/routes'
import {truthy} from 'src/utils/guards'

export enum CreateEntityButtonPlace {
  SIDEBAR = 'sidebar',
  ADMIN_SIDEBAR = 'adminSidebar',
  SIDEBAR_HEADER = 'sidebarHeader',
  ADMIN_SIDEBAR_HEADER = 'adminSidebarHeader',
  PROJECT_HEADER = 'projectHeader',
  SUBPROJECT = 'subproject',
  EDIT_PROJECT = 'editProject',
  EMPTY_SERVER_LANDING = 'emptyServerLanding',
  EMPTY_SEARCH_RESULTS = 'emptySearchResults',
}

type CreateEntityOptions = {
  createProject?: boolean
  createBuildType?: boolean
  createPipeline?: boolean
}
export function useCreateEntityMenu(
  place: CreateEntityButtonPlace,
  parentProjectId?: ProjectId,
  {
    createProject = true,
    createPipeline = true,
    createBuildType = true,
  }: CreateEntityOptions | undefined = {},
) {
  const isParentRoot = parentProjectId === ROOT_PROJECT_ID
  const isReadOnly = useAppSelector(state => getIsReadOnly(state, parentProjectId))
  const {isAdmin} = use(ProjectsTreeContext)

  const canRenderSection =
    !isReadOnly && parentProjectId != null && (createProject || createBuildType || createPipeline)

  const createProjectHref = parentProjectId && getCreateProjectHref(parentProjectId)
  const createProjectLinkClickHandler = useLinkClickHandler(createProjectHref ?? '')
  const createBuildTypeHref = parentProjectId && getCreateBuildTypeHref(parentProjectId)
  const createBuildTypeLinkClickHandler = useLinkClickHandler(createBuildTypeHref ?? '')
  const createPipelineHref = parentProjectId && getCreatePipelineHref(parentProjectId)
  const createPipelineLinkClickHandler = useLinkClickHandler(createPipelineHref ?? '')

  return useMemo(() => {
    if (!canRenderSection) {
      return []
    }

    const maybeItems: Array<ListDataItem | false> = [
      createBuildType &&
        !isParentRoot && {
          key: 'new-build-configuration',
          href: createBuildTypeHref,
          label: (
            <>
              <Icon className={styles.icon} glyph={buildType} />
              {'New build configuration'}
            </>
          ),
          onClick: (_, e) => {
            if (isCreateBuildTypeNewUiEnabled) {
              trackEvent({
                groupId: 'createflow.events.group',
                name: 'create.click',
                data: {type: 'buildType', place},
              })
            }
            if (isAdmin || !isCreateBuildTypeNewUiEnabled) {
              e.stopPropagation()
            } else {
              createBuildTypeLinkClickHandler(e as React.MouseEvent<HTMLAnchorElement>)
            }
          },
        },
      createPipeline &&
        !isParentRoot && {
          key: 'new-pipeline',
          href: createPipelineHref,
          label: (
            <>
              <Icon className={styles.icon} glyph={pipeline} />
              {'New pipeline'}
              {isEarlyAccessLabelEnabled && (
                <Tag
                  readOnly
                  textColor={'var(--ring-success-color)'}
                  backgroundColor={'var(--ring-added-background-color)'}
                  containerClassName={styles.earlyAccessTag}
                >
                  {'Early access'}
                </Tag>
              )}
            </>
          ),
          onClick: (_, e) => {
            trackEvent({
              groupId: 'createflow.events.group',
              name: 'create.click',
              data: {type: 'pipeline', place},
            })
            if (isAdmin) {
              e.stopPropagation()
            } else {
              createPipelineLinkClickHandler(e as React.MouseEvent<HTMLAnchorElement>)
            }
          },
        },
      createProject && {
        key: 'new-project',
        href: createProjectHref,
        label: (
          <>
            <Icon className={styles.icon} glyph={project} />
            {isParentRoot ? 'New project' : 'New subproject'}
          </>
        ),
        onClick: (_, e) => {
          if (isCreateProjectNewUiEnabled) {
            trackEvent({
              groupId: 'createflow.events.group',
              name: 'create.click',
              data: {type: 'project', place},
            })
          }
          if (isAdmin || !isCreateProjectNewUiEnabled) {
            e.stopPropagation()
          } else {
            createProjectLinkClickHandler(e as React.MouseEvent<HTMLAnchorElement>)
          }
        },
      },
    ]
    return maybeItems.filter(truthy)
  }, [
    canRenderSection,
    createBuildType,
    isParentRoot,
    createBuildTypeHref,
    createPipeline,
    createPipelineHref,
    createProject,
    createProjectHref,
    isAdmin,
    place,
    createBuildTypeLinkClickHandler,
    createPipelineLinkClickHandler,
    createProjectLinkClickHandler,
  ])
}

export function useCreateEntityButton(place: CreateEntityButtonPlace) {
  const projectId = useProjectId()
  const statusRequest = getProjectStatusRequest(projectId)

  useStatusSubscription({projectId})
  const projectPermissions = useAppSelector(state => {
    const statusObj: ProjectOrBuildTypeStatus | null | undefined = getStatus(state, statusRequest)
    return statusObj != null && 'projectPermissions' in statusObj
      ? statusObj.projectPermissions
      : null
  })

  const {canCreateSubProject, canEditProject, canCreatePipelines} = projectPermissions ?? {}
  const actions = useCreateEntityMenu(place, projectId, {
    createProject: canCreateSubProject,
    createBuildType: canEditProject,
    createPipeline: canCreatePipelines,
  })

  useHint(actions.length > 0 ? projectCreateHint : null)
  useSequenceLoaderReadyHandler(true)

  return actions
}
